import React, { useEffect, useState } from "react";
import { Wind, CloudRain, Thermometer } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { codeMap, raceCondition, LAT, LON } from "@/components/WeatherWidget";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export const TES_DATE = "2026-10-04";
export const WEATHER_WINDOW_DAYS = 16;   // Open-Meteo horizon
/* Tuscany Urban Cup — Campionato 2026 (from the official calendar poster) */
export const TUC_ROUNDS = [
    { n: 1, date: "2026-03-01", label: "01.03", place: "Casoli Camaiore" },
    { n: 2, date: "2026-04-26", label: "26.04", place: "Colle di Val d'Elsa" },
    { n: 3, date: "2026-05-31", label: "31.05", place: "Gerfalco" },
    { n: 4, date: "2026-06-21", label: "21.06", place: "Montelaterone" },
    { n: 5, date: "2026-09-13", label: "13.09", place: "San Cassiano di Controne" },
    { n: 6, date: "2026-10-11", label: "11.10", place: "Montenero" },
];
export const nextTucRound = (now = new Date()) => TUC_ROUNDS.find((r) => daysTo(r.date, now) >= 0) || null;
/* Official special stages of TES #6 (from toscanoenduroseries.it) */
export const TES_STAGES = {
    sassicaia: { ps: 1, km: "1.8" },
    noviziato: { ps: 2, km: "1.02" },
    madonnina: { ps: 3, km: "2.09" },
    senal: { ps: 4, km: "2" },
};

export function StageBadge({ slug, size = "sm", className = "" }) {
    const st = TES_STAGES[slug];
    if (!st) return null;
    const cls = size === "lg"
        ? "px-3 py-1.5 text-[11px]"
        : "px-2 py-1 text-[10px]";
    return (
        <span data-testid={`stage-badge-${slug}`} className={`inline-flex items-center gap-1.5 bg-abnormal-magenta text-black font-vt uppercase tracking-[0.25em] ${cls} ${className}`}>
            PS{st.ps} · TES{size === "lg" && <span className="opacity-70">· {st.km} km</span>}
        </span>
    );
}

/* Days until a date (calendar days, local time). 0 = today, <0 = past. */
export function daysTo(iso, now = new Date()) {
    const [y, m, d] = iso.split("-").map(Number);
    const race = new Date(y, m - 1, d);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((race - today) / 86400000);
}
export const daysToRace = (now = new Date()) => daysTo(TES_DATE, now);

function countdownLabel(days, en) {
    if (days > 1) return en ? "days to go" : "giorni alla gara";
    if (days === 1) return en ? "day to go" : "giorno alla gara";
    if (days === 0) return en ? "race day" : "oggi si corre";
    return en ? "race done" : "gara conclusa";
}

export function Countdown({ lang, className = "" }) {
    const en = lang === "en";
    const days = daysToRace();
    const label = countdownLabel(days, en);
    return (
        <div data-testid="tes-countdown" className={`border border-white/15 bg-abnormal-charcoal px-4 py-3 flex items-center justify-between gap-4 ${className}`}>
            <div className="flex items-baseline gap-3">
                <span className="label-mono text-abnormal-acid">[ COUNTDOWN ]</span>
                {days > 0 && <span data-testid="tes-countdown-days" className="font-display text-5xl md:text-6xl leading-none text-abnormal-paper">{days}</span>}
                <span className="font-display uppercase text-xl md:text-2xl leading-none text-abnormal-paperDim">{label}</span>
            </div>
            <span className="label-mono text-abnormal-paperDim/60 hidden sm:inline">04 · 10 · 2026</span>
        </div>
    );
}

/* Slim home strip — "Prossima gara · 4 ottobre" → poster on /eventi#tes */
function useLiveWeather() {
    const [w, setW] = useState(null);
    useEffect(() => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/weather?latitude=${LAT}&longitude=${LON}&current=temperature_2m,weather_code,wind_speed_10m&timezone=Europe%2FRome`;
        fetch(url, { cache: "no-store" }).then((r) => r.json()).then((j) => j.current && setW(j.current)).catch(() => {});
    }, []);
    return w;
}

const BAND_INTERVAL = 6000;
const BAND_FADE = 400;

/* Alternates TES 04.10 ↔ next TUC round (crossfade); TES drops out after race day, TUC after 11.10. */
export function NextRaceBand({ lang }) {
    const en = lang === "en";
    const { t } = useLang();
    const days = daysToRace();
    const tuc = nextTucRound();
    const w = useLiveWeather();
    const WIcon = w ? codeMap(w.weather_code).Icon : null;
    const items = [];
    if (days >= 0) items.push("tes");
    if (tuc) items.push("tuc");
    const [idx, setIdx] = useState(0);
    const [show, setShow] = useState(true);
    useEffect(() => {
        if (items.length < 2) return;
        const iv = setInterval(() => {
            setShow(false);
            setTimeout(() => { setIdx((i) => (i + 1) % items.length); setShow(true); }, BAND_FADE);
        }, BAND_INTERVAL);
        return () => clearInterval(iv);
    }, [items.length]);
    if (!items.length) return null;
    const kind = items[idx % items.length];
    const isTes = kind === "tes";
    const tucDays = tuc ? daysTo(tuc.date) : 0;
    const dLeft = isTes ? days : tucDays;
    const dateLabel = isTes ? (en ? "4 Oct" : "4 ottobre")
        : new Date(tuc.date + "T00:00:00").toLocaleDateString(en ? "en-GB" : "it-IT", { day: "numeric", month: en ? "short" : "long" });
    const Dot = ({ className = "" }) => <span className={`w-1.5 h-1.5 rounded-full bg-abnormal-acid ${className}`} />;
    return (
        <Link to={isTes ? "/eventi#tes" : "/eventi#tuc"} data-testid="home-next-race" data-race={kind}
            className="group block border-y border-white/10 bg-abnormal-charcoal hover:bg-abnormal-charcoal2 transition-colors">
            <div className={`max-w-[1600px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap transition-opacity duration-[400ms] ${show ? "opacity-100" : "opacity-0"}`}>
                <p className="flex items-center gap-3 flex-wrap font-display uppercase leading-none tracking-tight text-abnormal-paper text-lg md:text-2xl">
                    <span className="label-mono text-abnormal-acid">{en ? "Next race" : "Prossima gara"} · {dateLabel}</span>
                    <Dot />
                    {isTes ? (
                        <>
                            <span>Toscano Enduro Series</span>
                            <Dot />
                            <span className="text-abnormal-acid">{en ? "The course is out." : "Il giro è fuori."}</span>
                            <Dot className="hidden sm:inline" />
                            <span className="text-abnormal-paperDim hidden sm:inline">Monte Argentario · {en ? "Abnormal prizes" : "Premi Abnormal"}</span>
                        </>
                    ) : (
                        <>
                            <span>Tuscany Urban Cup</span>
                            <Dot />
                            <span className="text-abnormal-acid" data-testid="home-next-race-tuc-round">R{tuc.n} · {tuc.place}</span>
                            <Dot className="hidden sm:inline" />
                            <span className="text-abnormal-paperDim hidden sm:inline">{en ? "ABnormal official sponsor" : "ABnormal sponsor ufficiale"}</span>
                        </>
                    )}
                </p>
                <span className="flex items-center gap-5 flex-wrap">
                    {isTes && w && (
                        <span data-testid="home-live-weather" className="label-mono text-abnormal-paperDim inline-flex items-center gap-2">
                            <WIcon size={14} className="text-abnormal-acid" />
                            {en ? "Argentario now" : "Argentario ora"} · {Math.round(w.temperature_2m)}° · {t(`weather.codes.${codeMap(w.weather_code).key}`)} · <Wind size={11} /> {Math.round(w.wind_speed_10m)} km/h
                        </span>
                    )}
                    <span className="label-mono text-abnormal-acid inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                        {dLeft > 0 ? `-${dLeft} ${en ? "days" : "giorni"} · ` : ""}{isTes ? (en ? "Poster" : "Locandina") : (en ? "Calendar" : "Calendario")} <ArrowRight size={13} />
                    </span>
                </span>
            </div>
        </Link>
    );
}

/* "Conditions on the ground" — admin note posted on race morning (settings.tes_ground_note). Hidden when empty. */
export function GroundNote({ lang, className = "" }) {
    const en = lang === "en";
    const [s, setS] = useState(null);
    useEffect(() => {
        api.get("/settings").then((r) => setS(r.data || {})).catch(() => setS({}));
    }, []);
    const note = s ? ((en && s.tes_ground_note_en) || s.tes_ground_note || s.tes_ground_note_en) : "";
    if (!note) return null;
    const at = s.tes_ground_note_at ? new Date(s.tes_ground_note_at) : null;
    const when = at && !isNaN(at) ? at.toLocaleString(en ? "en-GB" : "it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "";
    return (
        <div data-testid="tes-ground-note" className={`border border-abnormal-acid/60 border-l-4 border-l-abnormal-acid bg-abnormal-charcoal px-4 py-3 ${className}`}>
            <div className="flex items-center justify-between gap-3">
                <span className="label-mono text-abnormal-acid inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-abnormal-acid animate-pulse" />
                    [ {en ? "Conditions on the ground" : "Condizioni sul campo"} ]
                </span>
                {when && <span data-testid="tes-ground-note-at" className="label-mono text-abnormal-paperDim/60">{en ? "updated" : "aggiornato"} {when}</span>}
            </div>
            <p data-testid="tes-ground-note-text" className="mt-2 text-abnormal-paper text-sm md:text-base leading-relaxed">{note}</p>
            <p className="mt-1.5 font-vt text-[10px] tracking-[0.2em] text-abnormal-paperDim/70">— ABNORMAL CREW · LIVE</p>
        </div>
    );
}

/* Race-day forecast (Open-Meteo, Argentario). Shown only when race day is < 7 days away. */
export function RaceWeather({ lang, className = "" }) {
    const { t } = useLang();
    const en = lang === "en";
    const days = daysToRace();
    const force = typeof window !== "undefined" && window.location.search.includes("forceweather");
    const visible = force || (days >= 0 && days <= WEATHER_WINDOW_DAYS);
    const indicative = days >= 7;
    const [d, setD] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        if (!visible) return;
        const day = force && days > WEATHER_WINDOW_DAYS ? new Date().toISOString().slice(0, 10) : TES_DATE;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/weather?latitude=${LAT}&longitude=${LON}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max&start_date=${day}&end_date=${day}&timezone=Europe%2FRome`;
        fetch(url, { cache: "no-store" }).then((r) => r.json()).then((j) => {
            const dy = j.daily;
            if (!dy?.time?.length) throw new Error("no data");
            setD({ code: dy.weather_code[0], tmax: dy.temperature_2m_max[0], tmin: dy.temperature_2m_min[0], rain: dy.precipitation_sum[0], prob: dy.precipitation_probability_max?.[0], wind: dy.wind_speed_10m_max[0] });
        }).catch(() => setErr(true));
    }, [visible, force, days]);
    if (!visible) return null;
    const { Icon, key, lowRain } = d ? raceCondition(d.code, d.prob, d.rain) : { Icon: Thermometer, key: "unknown", lowRain: false };
    return (
        <div data-testid="tes-race-weather" className={`border border-white/15 bg-abnormal-charcoal px-4 py-3 ${className}`}>
            <div className="flex items-center justify-between gap-3">
                <span className="label-mono text-abnormal-acid">[ {en ? "Race day forecast" : "Meteo gara"} ]</span>
                <span className="label-mono text-abnormal-paperDim/60">04 · 10 · Argentario{indicative ? (en ? " · indicative" : " · indicativa") : ""}</span>
            </div>
            {err ? (
                <p className="mt-2 label-mono text-abnormal-paperDim/60">{en ? "Forecast unavailable" : "Previsione non disponibile"}</p>
            ) : !d ? (
                <p className="mt-2 label-mono text-abnormal-paperDim/60">{en ? "Loading…" : "Caricamento…"}</p>
            ) : (
                <div className="mt-2 flex items-center gap-4 flex-wrap">
                    <Icon size={34} className="text-abnormal-acid flex-shrink-0" />
                    <div>
                        <p className="font-display uppercase text-2xl md:text-3xl leading-none text-abnormal-paper">{t(`weather.codes.${key}`)}</p>
                        <p className="mt-1 label-mono text-abnormal-paperDim flex items-center gap-3 flex-wrap">
                            <span data-testid="tes-race-weather-temp">{Math.round(d.tmin)}° / {Math.round(d.tmax)}°</span>
                            <span data-testid="tes-race-weather-rain" className="inline-flex items-center gap-1"><CloudRain size={11} />
                                {lowRain ? `${d.prob}% · ${en ? "a few drops" : "qualche goccia"}` : `${d.rain} mm${d.prob != null ? ` · ${d.prob}%` : ""}`}
                            </span>
                            <span className="inline-flex items-center gap-1"><Wind size={11} /> {Math.round(d.wind)} km/h</span>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

/* Hourly forecast for race day (07→17), shown only when < 3 days to go. */
export function HourlyRaceWeather({ lang, className = "" }) {
    const en = lang === "en";
    const days = daysToRace();
    const force = typeof window !== "undefined" && window.location.search.includes("forcehourly");
    const visible = force || (days >= 0 && days < 3);
    const [rows, setRows] = useState(null);
    useEffect(() => {
        if (!visible) return;
        const day = force && days >= 3 ? new Date().toISOString().slice(0, 10) : TES_DATE;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/weather?latitude=${LAT}&longitude=${LON}&hourly=temperature_2m,weather_code,precipitation_probability,wind_speed_10m&start_date=${day}&end_date=${day}&timezone=Europe%2FRome`;
        fetch(url, { cache: "no-store" }).then((r) => r.json()).then((j) => {
            const h = j.hourly;
            const out = [];
            h.time.forEach((tm, i) => {
                const hour = Number(tm.slice(11, 13));
                if (hour >= 7 && hour <= 17) out.push({ hour, t: h.temperature_2m[i], code: h.weather_code[i], prob: h.precipitation_probability[i], wind: h.wind_speed_10m[i] });
            });
            setRows(out);
        }).catch(() => setRows([]));
    }, [visible, force, days]);
    if (!visible) return null;
    const MARK = { 7: en ? "Checks" : "Verifiche", 8: en ? "Start" : "Partenza", 15: en ? "Last finisher" : "Ultimo arrivo", 16: en ? "Podium" : "Premiazioni" };
    return (
        <div data-testid="tes-hourly-weather" className={`border border-white/15 bg-abnormal-charcoal ${className}`}>
            <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-white/10">
                <span className="label-mono text-abnormal-acid">[ {en ? "Race day · hour by hour" : "Giorno gara · ora per ora"} ]</span>
                <span className="label-mono text-abnormal-paperDim/60">04 · 10 · 07:00 → 17:00</span>
            </div>
            {!rows ? <p className="px-4 py-3 label-mono text-abnormal-paperDim/60">…</p>
                : rows.length === 0 ? <p className="px-4 py-3 label-mono text-abnormal-paperDim/60">{en ? "Forecast unavailable" : "Previsione non disponibile"}</p>
                : (
                <div className="overflow-x-auto">
                    <div className="flex min-w-max divide-x divide-white/10">
                        {rows.map((r) => {
                            const { Icon } = raceCondition(r.code, r.prob, null);
                            const mark = MARK[r.hour];
                            return (
                                <div key={r.hour} data-testid={`tes-hour-${r.hour}`} className={`w-[84px] px-2 py-3 text-center ${mark ? "bg-white/[0.03]" : ""}`}>
                                    <p className="font-vt text-[10px] tracking-[0.2em] text-abnormal-paperDim">{String(r.hour).padStart(2, "0")}:00</p>
                                    <Icon size={18} className="mx-auto mt-1.5 text-abnormal-acid" />
                                    <p className="font-display text-2xl leading-none mt-1.5 text-abnormal-paper">{Math.round(r.t)}°</p>
                                    <p className="font-vt text-[9px] tracking-[0.1em] text-abnormal-paperDim/70 mt-1">{r.prob}% · {Math.round(r.wind)}km/h</p>
                                    {mark && <p className="mt-1.5 font-vt text-[9px] tracking-[0.15em] uppercase text-abnormal-magenta leading-tight">{mark}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
